import {
  formatMarketingCount,
  formatMarketingPct,
  IMarketingDelta,
  IMarketingRange,
  isMarketingIsoDay,
  marketingDelta,
  marketingNumber,
  marketingRangeDays,
  marketingShiftDay,
  MARKETING_DASH,
  MarketingMetricDirection,
} from './marketingModel';
import {
  IMarketingAiReferralRow,
  IMarketingDemandRow,
  IMarketingFilter,
  IMarketingSearchPoint,
  IMarketingSearchRow,
  MarketingDate,
  MarketingSearchDimensionCode,
  MarketingSearchEngineCode,
  MarketingSearchZoneCode,
  MarketingTrafficGroupCode,
} from './types';

/**
 * ТЗ 0088, этап 3 — чистые помощники вкладки «Поиск и сайт» (без React,
 * Apollo и i18n: подписи — ключи `marketing.search.*`).
 *
 * Правила, которые держит этот модуль:
 *  - **Google и Bing не складываются нигде**: у каждого свой отчёт, свои
 *    плитки и свой ряд, общей суммы нет ни в одной функции;
 *  - **пропуск ≠ ноль**: несобранный день — не «0 кликов», прошлое окно,
 *    собранное не целиком, — не база для изменения (`prev*` NULL →
 *    прочерк, а не «+100 %»);
 *  - итог GSC — полная цифра кликов, строки запросов — неполный срез
 *    (`namedClicksShare`): строки никогда не суммируются в «итог».
 */

/* ── Словари ─────────────────────────────────────────────────────────── */

export const MARKETING_SEARCH_ENGINES: MarketingSearchEngineCode[] = [
  'google',
  'bing',
];

export const MARKETING_SEARCH_DIMENSIONS: MarketingSearchDimensionCode[] = [
  'query',
  'page',
  'query_page',
  'country',
  'device',
];

/** Bing отдаёт снимки только запросов и страниц. */
export const MARKETING_BING_DIMENSIONS: MarketingSearchDimensionCode[] = [
  'query',
  'page',
];

export const MARKETING_DEFAULT_SEARCH_DIMENSION: MarketingSearchDimensionCode =
  'query';

export const isMarketingSearchDimension = (
  value: string | null | undefined,
): value is MarketingSearchDimensionCode =>
  Boolean(value) &&
  (MARKETING_SEARCH_DIMENSIONS as string[]).includes(value as string);

/** Зоны средней позиции — в порядке «от вершины выдачи вниз». */
export const MARKETING_SEARCH_ZONES: MarketingSearchZoneCode[] = [
  '1_3',
  '4_10',
  '11_20',
  '21_50',
  '51plus',
];

export const isMarketingSearchZone = (
  value: string | null | undefined,
): value is MarketingSearchZoneCode =>
  Boolean(value) &&
  (MARKETING_SEARCH_ZONES as string[]).includes(value as string);

/** Подпись зоны — числа, одинаковые на обоих языках. */
export const MARKETING_SEARCH_ZONE_LABELS: Readonly<
  Record<MarketingSearchZoneCode, string>
> = {
  '1_3': '1–3',
  '4_10': '4–10',
  '11_20': '11–20',
  '21_50': '21–50',
  '51plus': '51+',
};

export const marketingSearchZoneLabel = (zone: string): string =>
  isMarketingSearchZone(zone) ? MARKETING_SEARCH_ZONE_LABELS[zone] : zone;

/** Фильтр бренда в адресе: `brand` — только брендовые, `nonbrand` — без них. */
/**
 * Бренд адреса (`brand`): только брендовые / без бренда; `all` — явно
 * «все» (повторное ревью 0103, A16: у SEO › «Позиции» по умолчанию — без
 * бренда, и «все» нужно назвать в адресе). Search Console читает `all` как
 * «все» — тем же, что пустой параметр.
 */
export type MarketingSearchBrandFilter = 'brand' | 'nonbrand' | 'all';

export const MARKETING_SEARCH_BRAND_FILTERS: MarketingSearchBrandFilter[] = [
  'nonbrand',
  'brand',
];

export const isMarketingSearchBrandFilter = (
  value: string | null | undefined,
): value is MarketingSearchBrandFilter =>
  value === 'brand' || value === 'nonbrand' || value === 'all';

/** Есть ли у строк разреза признак бренда (он — у запроса). */
export const marketingSearchHasBrand = (
  dimension: MarketingSearchDimensionCode,
): boolean => dimension === 'query' || dimension === 'query_page';

/**
 * Переменная `brand` запроса. У страниц, стран и устройств бренда нет —
 * фильтр к ним не применяется (сервер отдал бы пустую таблицу).
 */
export const marketingSearchBrandVar = (
  filter: MarketingSearchBrandFilter | null,
  dimension: MarketingSearchDimensionCode,
): boolean | null => {
  if (!filter || filter === 'all' || !marketingSearchHasBrand(dimension)) {
    return null;
  }
  return filter === 'brand';
};

export const MARKETING_TRAFFIC_GROUPS: MarketingTrafficGroupCode[] = [
  'channel',
  'source',
  'campaign',
];

/** Группировка трафика GA4 по умолчанию — группа каналов. */
export const MARKETING_DEFAULT_TRAFFIC_GROUP: MarketingTrafficGroupCode =
  'channel';

export const isMarketingTrafficGroup = (
  value: string | null | undefined,
): value is MarketingTrafficGroupCode =>
  Boolean(value) &&
  (MARKETING_TRAFFIC_GROUPS as string[]).includes(value as string);

/** Разрез строк снимка Bing по умолчанию — запросы. */
export const MARKETING_DEFAULT_BING_DIMENSION: MarketingSearchDimensionCode =
  'query';

/** Разрез Bing из адреса: только запросы и страницы (других снимков нет). */
export const isMarketingBingDimension = (
  value: string | null | undefined,
): value is MarketingSearchDimensionCode =>
  Boolean(value) &&
  (MARKETING_BING_DIMENSIONS as string[]).includes(value as string);

/**
 * Фильтр поиска и сайта — только окно. Каналы, курсы, офферы, юрлицо и
 * модель атрибуции к Search Console и GA4 не относятся (вкладка говорит об
 * этом баннером) — в переменные их не кладём: иначе одна и та же цифра
 * кешировалась бы под каждым сочетанием чужих фильтров, а продажи спроса
 * молча сузились бы невидимым юрлицом. Панель AI-ассистента справа строит
 * тот же фильтр — и попадает в тот же ответ кеша.
 */
export const marketingSiteFilter = (
  filter: IMarketingFilter,
): IMarketingFilter => ({
  dateFrom: filter.dateFrom,
  dateTo: filter.dateTo,
});

/** Устройства GSC — со своей подписью; прочее — как пришло. */
export const MARKETING_SEARCH_DEVICES = ['desktop', 'mobile', 'tablet'];

export const marketingSearchDeviceKey = (
  code: string | null | undefined,
): string | null => {
  const lower = (code ?? '').toLowerCase();
  return MARKETING_SEARCH_DEVICES.includes(lower)
    ? `marketing.search.devices.${lower}`
    : null;
};

/**
 * AI-ассистенты — названия продуктов, одинаковые на обоих языках (не
 * перевод). Незнакомый код — как пришёл.
 */
export const MARKETING_AI_ASSISTANT_NAMES: Readonly<Record<string, string>> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  copilot: 'Copilot',
  perplexity: 'Perplexity',
  deepseek: 'DeepSeek',
  mistral: 'Mistral',
  you: 'You.com',
};

export const marketingAiAssistantName = (code: string): string =>
  MARKETING_AI_ASSISTANT_NAMES[code.toLowerCase()] ?? code;

/* ── Строки ──────────────────────────────────────────────────────────── */

/**
 * Адрес страницы без схемы и завершающего слеша (`https://learnit.com.pl/x/`
 * → `learnit.com.pl/x`): хост оставляем — ресурс GSC доменный, и страницы
 * lms. и webinars. не должны выглядеть страницами сайта.
 */
export const marketingPageLabel = (url: string): string => {
  const trimmed = url.trim().replace(/^https?:\/\//i, '');
  return trimmed.length > 1 ? trimmed.replace(/\/+$/, '') || trimmed : trimmed;
};

/**
 * Хост страницы, если он НЕ свой (`MarketingSettings.siteHosts`): страница
 * lms. или webinars. в доменном ресурсе GSC — не посадочная сайта, её
 * помечаем хостом. Свои хосты не известны (справочник не пришёл) или хоста
 * нет — `null`: пометки нет, а не «всё чужое».
 */
export const marketingForeignHost = (
  host: string | null | undefined,
  siteHosts: ReadonlyArray<string> | null | undefined,
): string | null => {
  const value = (host ?? '').trim().toLowerCase();
  if (!value || !siteHosts) return null;
  const own = siteHosts.map((item) => item.trim().toLowerCase());
  return own.includes(value) ? null : value;
};

/** Ссылка наружу — только на http(s): прочее рисуется текстом. */
export const marketingExternalHref = (url: string): string | null =>
  /^https?:\/\//i.test(url.trim()) ? url.trim() : null;

/** CTR: клики / показы; показов нет — `null`, а не «0 %». */
export const marketingSearchCtr = (
  clicks: number | null | undefined,
  impressions: number | null | undefined,
): number | null => {
  const c = marketingNumber(clicks);
  const i = marketingNumber(impressions);
  if (c === null || i === null || i <= 0) return null;
  return c / i;
};

/** Доля вовлечённых сессий; сессий нет — `null`. */
export const marketingEngagementRate = (
  engaged: number | null | undefined,
  sessions: number | null | undefined,
): number | null => {
  const e = marketingNumber(engaged);
  const s = marketingNumber(sessions);
  if (e === null || s === null || s <= 0) return null;
  return e / s;
};

/**
 * Изменение числа строки к прошлому окну.
 *  - `unknown` — прошлое окно собрано не целиком (`prev*` NULL): прочерк;
 *  - `new` — в прошлом окне ключа не было (0 → больше нуля);
 *  - иначе — разница со знаком и тоном (`direction` показателя: позиция
 *    «лучше вниз»).
 */
export type MarketingRowChange =
  | { kind: 'unknown' }
  | { kind: 'new' }
  | { kind: 'diff'; diff: number; tone: IMarketingDelta['tone'] };

export const marketingRowChange = (
  current: number | null | undefined,
  previous: number | null | undefined,
  direction: MarketingMetricDirection,
): MarketingRowChange => {
  const cur = marketingNumber(current);
  const prev = marketingNumber(previous);
  if (cur === null || prev === null) return { kind: 'unknown' };
  const delta = marketingDelta(cur, prev, direction);
  if (!delta) return { kind: 'unknown' };
  if (prev === 0 && cur > 0 && direction === 'up') return { kind: 'new' };
  return { kind: 'diff', diff: delta.diff, tone: delta.tone };
};

/**
 * Изменение позиции строки. Позиции в прошлом окне нет, а показы там
 * собраны (0) — ключ новый; окно не собрано (`prevImpressions` NULL) —
 * неизвестно.
 */
export const marketingPositionChange = (
  row: Pick<
    IMarketingSearchRow,
    'position' | 'prevPosition' | 'prevImpressions'
  >,
): MarketingRowChange => {
  const cur = marketingNumber(row.position);
  const prev = marketingNumber(row.prevPosition);
  if (cur !== null && prev === null && row.prevImpressions === 0) {
    return { kind: 'new' };
  }
  if (cur === null || prev === null) return { kind: 'unknown' };
  const delta = marketingDelta(
    Math.round(cur * 10) / 10,
    Math.round(prev * 10) / 10,
    'down',
  );
  return delta
    ? { kind: 'diff', diff: delta.diff, tone: delta.tone }
    : { kind: 'unknown' };
};

/** Что сортирует сервер в таблице поисковика (`orderBy`). */
export type MarketingSearchSortKey =
  | 'key'
  | 'clicks'
  | 'impressions'
  | 'ctr'
  | 'position';

/** Что сортирует сервер в поисковых запросах Google Ads (`orderBy`). */
export type MarketingSearchTermSortKey =
  | 'term'
  | 'spendPln'
  | 'clicks'
  | 'impressions'
  | 'conversions';

/* ── Плитки и сравнение окон ─────────────────────────────────────────── */

/** Дни окна по порядку, оба края включительно. */
export const marketingRangeDayList = (
  range: IMarketingRange,
): MarketingDate[] =>
  isMarketingIsoDay(range.from) && isMarketingIsoDay(range.to)
    ? Array.from({ length: Math.max(0, marketingRangeDays(range)) }, (_, i) =>
        marketingShiftDay(range.from, i),
      )
    : [];

/** Сколько дней окна поисковик собрал (есть точка ряда). */
export const marketingCollectedDays = (
  series: ReadonlyArray<{ day: MarketingDate }>,
  range: IMarketingRange,
): number => {
  const days = new Set(series.map((point) => point.day));
  return marketingRangeDayList(range).filter((day) => days.has(day)).length;
};

/**
 * Как сравнивать окно с прошлым:
 *  - `total` — оба окна собраны целиком: итог против итога;
 *  - `perDay` — прошлое собрано, а в текущем не хватает свежих дней
 *    (поисковик отстаёт на 2–3 дня, сегодня не собран никогда): сравниваем
 *    клики и показы В СРЕДНЕМ ЗА СОБРАННЫЙ ДЕНЬ, иначе рост «съедался» бы
 *    несобранными днями;
 *  - `none` — прошлое окно собрано не целиком или текущее пусто: изменения
 *    нет (пропуск ≠ ноль).
 */
export type MarketingSearchCompareBasis = 'total' | 'perDay' | 'none';

export interface IMarketingSearchComparison {
  basis: MarketingSearchCompareBasis;
  /** Собрано дней текущего окна. */
  collected: number;
  /** Дней в окне. */
  days: number;
  /** Собрано дней прошлого окна. */
  previousCollected: number;
}

export const marketingSearchComparison = (
  currentSeries: ReadonlyArray<{ day: MarketingDate }>,
  previousSeries: ReadonlyArray<{ day: MarketingDate }> | null | undefined,
  range: IMarketingRange,
  previousRange: IMarketingRange,
): IMarketingSearchComparison => {
  const days = marketingRangeDays(range);
  const collected = marketingCollectedDays(currentSeries, range);
  const previousCollected = previousSeries
    ? marketingCollectedDays(previousSeries, previousRange)
    : 0;
  let basis: MarketingSearchCompareBasis = 'none';
  if (
    previousSeries &&
    previousCollected === marketingRangeDays(previousRange) &&
    collected > 0
  ) {
    basis = collected === days ? 'total' : 'perDay';
  }
  return { basis, collected, days, previousCollected };
};

export type MarketingSearchKpiKey =
  | 'clicks'
  | 'impressions'
  | 'ctr'
  | 'position';

export const MARKETING_SEARCH_KPIS: MarketingSearchKpiKey[] = [
  'clicks',
  'impressions',
  'ctr',
  'position',
];

export interface IMarketingSearchKpi {
  key: MarketingSearchKpiKey;
  value: number | null;
  /** Итог прошлого окна (как есть, без пересчёта на день). */
  previous: number | null;
  /** Изменение; у `perDay` — по среднему за собранный день. */
  delta: IMarketingDelta | null;
  direction: MarketingMetricDirection;
}

const KPI_DIRECTION: Record<MarketingSearchKpiKey, MarketingMetricDirection> = {
  clicks: 'up',
  impressions: 'up',
  ctr: 'up',
  // Позиция 1 — вершина выдачи: меньше — лучше.
  position: 'down',
};

const kpiValue = (
  point: IMarketingSearchPoint | null | undefined,
  key: MarketingSearchKpiKey,
): number | null => {
  if (!point) return null;
  if (key === 'ctr') return marketingSearchCtr(point.clicks, point.impressions);
  if (key === 'position') return marketingNumber(point.position);
  return marketingNumber(point[key]);
};

/** Счётчик на день: итог окна / собранные дни. */
const perDay = (value: number | null, days: number): number | null =>
  value === null || days <= 0 ? null : value / days;

/**
 * Плитки поисковика: клики, показы, CTR, средняя позиция — с изменением к
 * прошлому окну по правилу `comparison.basis`. Позиция сравнивается
 * абсолютной разницей (десятые), остальное — процентом.
 */
export const marketingSearchKpis = (
  current: IMarketingSearchPoint | null | undefined,
  previous: IMarketingSearchPoint | null | undefined,
  comparison: IMarketingSearchComparison,
): IMarketingSearchKpi[] =>
  MARKETING_SEARCH_KPIS.map((key) => {
    const value = kpiValue(current, key);
    const before = previous ? kpiValue(previous, key) : null;
    const direction = KPI_DIRECTION[key];
    let delta: IMarketingDelta | null = null;
    if (comparison.basis !== 'none' && previous) {
      if (key === 'position') {
        delta = marketingDelta(
          value === null ? null : Math.round(value * 10) / 10,
          before === null ? null : Math.round(before * 10) / 10,
          direction,
        );
      } else if (
        comparison.basis === 'perDay' &&
        (key === 'clicks' || key === 'impressions')
      ) {
        delta = marketingDelta(
          perDay(value, comparison.collected),
          perDay(before, comparison.previousCollected),
          direction,
        );
      } else {
        // CTR — доля: разница в п. п. (повторное ревью 0103, A12).
        delta = marketingDelta(
          value,
          before,
          direction,
          key === 'ctr' ? 'rate' : null,
        );
      }
    }
    return { key, value, previous: before, delta, direction };
  });

/* ── Ряд: провизорные дни ────────────────────────────────────────────── */

/** День ряда провизорный (источник его ещё пересчитывает). */
export const marketingIsProvisional = (
  day: MarketingDate,
  provisionalFrom: MarketingDate | null | undefined,
): boolean => Boolean(provisionalFrom) && day >= (provisionalFrom as string);

/* ── Доли ────────────────────────────────────────────────────────────── */

/**
 * «≈ 31 %» — доля целыми процентами: раскрытые клики и покрытие расхода —
 * оценка, десятые там ложная точность. `null` — прочерк.
 */
export const formatMarketingApproxPct = (
  value: number | null | undefined,
  lang?: string | null,
): string => {
  const number = marketingNumber(value);
  if (number === null) return MARKETING_DASH;
  const rounded = Math.round(number * 100) / 100;
  return formatMarketingPct(rounded, lang);
};

/**
 * Доли зон для полосы: ширина — по показам (кликов в нижних зонах почти
 * нет, по ним полоса была бы одной зоной). Пустая зона — 0, сумма 1.
 */
export const marketingZoneShares = (
  zones: ReadonlyArray<{ zone: string; impressions: number }>,
): Record<string, number> => {
  const total = zones.reduce((sum, zone) => sum + (zone.impressions || 0), 0);
  return Object.fromEntries(
    zones.map((zone) => [
      zone.zone,
      total > 0 ? (zone.impressions || 0) / total : 0,
    ]),
  );
};

/* ── AI-ассистенты ───────────────────────────────────────────────────── */

export interface IMarketingAiAssistant {
  assistant: string;
  sessions: number;
  engaged: number;
  /** Посадочные ассистента — по сессиям. */
  landings: IMarketingAiReferralRow[];
}

/**
 * Итог по ассистенту: сервер отдаёт строку на «ассистент × посадочная», у
 * сессии одна посадочная — строки ассистента складываются в его итог.
 * Больше сессий — выше; равные — по имени.
 */
export const marketingAiAssistants = (
  rows: ReadonlyArray<IMarketingAiReferralRow>,
): IMarketingAiAssistant[] => {
  const map = new Map<string, IMarketingAiAssistant>();
  rows.forEach((row) => {
    const current = map.get(row.assistant) ?? {
      assistant: row.assistant,
      sessions: 0,
      engaged: 0,
      landings: [],
    };
    map.set(row.assistant, {
      ...current,
      sessions: current.sessions + (row.sessions || 0),
      engaged: current.engaged + (row.engaged || 0),
      landings: [...current.landings, row],
    });
  });
  return Array.from(map.values())
    .map((item) => ({
      ...item,
      landings: [...item.landings].sort(
        (a, b) =>
          b.sessions - a.sessions ||
          (a.landingPage ?? '').localeCompare(b.landingPage ?? ''),
      ),
    }))
    .sort(
      (a, b) =>
        b.sessions - a.sessions || a.assistant.localeCompare(b.assistant),
    );
};

/**
 * Ассистент панели справа (`insp=ai:<код>`) — из УЖЕ загруженных строк
 * отчёта: итог и посадочные тем же правилом, что у таблицы. Нет строк
 * ассистента за окно — `null` («не найдено за это окно»).
 */
export const marketingAiAssistantOf = (
  rows: ReadonlyArray<IMarketingAiReferralRow>,
  assistant: string,
): IMarketingAiAssistant | null =>
  marketingAiAssistants(rows.filter((row) => row.assistant === assistant))[0] ??
  null;

/* ── Спрос ───────────────────────────────────────────────────────────── */

export interface IMarketingDemandSplit {
  /** `isSold=false` со спросом в окне — «не продвигаем». */
  unserved: IMarketingDemandRow[];
  /** Продаваемые семейства — спрос с трендом. */
  sold: IMarketingDemandRow[];
  /** Непродаваемые без единого показа, лида и прошлого спроса — именами. */
  quiet: IMarketingDemandRow[];
}

const byImpressions = (a: IMarketingDemandRow, b: IMarketingDemandRow) =>
  b.impressions - a.impressions ||
  b.crmLeads - a.crmLeads ||
  a.familyName.localeCompare(b.familyName);

/**
 * Разложить спрос: семейства, которые мы не продаём, но их ищут (или о них
 * спрашивают в CRM), — отдельно от продаваемых. Непродаваемое семейство без
 * показов, лидов и прошлого спроса — не сигнал, его имя уходит в «тихие».
 */
export const marketingDemandSplit = (
  rows: ReadonlyArray<IMarketingDemandRow>,
): IMarketingDemandSplit => {
  const unserved: IMarketingDemandRow[] = [];
  const quiet: IMarketingDemandRow[] = [];
  const sold: IMarketingDemandRow[] = [];
  rows.forEach((row) => {
    if (row.isSold) sold.push(row);
    else if (
      row.impressions > 0 ||
      row.crmLeads > 0 ||
      (row.prevImpressions ?? 0) > 0
    ) {
      unserved.push(row);
    } else quiet.push(row);
  });
  return {
    unserved: unserved.sort(byImpressions),
    sold: sold.sort(byImpressions),
    quiet: quiet.sort(byImpressions),
  };
};

/** Изменение показов семейства к прошлому окну (`null` — не собрано). */
export const marketingDemandDelta = (
  row: Pick<IMarketingDemandRow, 'impressions' | 'prevImpressions'>,
): IMarketingDelta | null =>
  marketingDelta(row.impressions, row.prevImpressions ?? null, 'up');

/** Топ-запросы семейства одной строкой: «kurs devops (172), …». */
export const marketingTopQueriesLabel = (
  rows: ReadonlyArray<Pick<IMarketingSearchRow, 'key' | 'impressions'>>,
  lang?: string | null,
  limit = 3,
): string =>
  rows
    .slice(0, limit)
    .map((row) => `${row.key} (${formatMarketingCount(row.impressions, lang)})`)
    .join(', ');

/* ── Посадочные: ключевые события ────────────────────────────────────── */

/**
 * Колонки ключевых событий: список из настроек (он и считается ключевым);
 * справочник ещё не пришёл — события первой строки, чтобы колонка не
 * исчезла на миг.
 */
export const marketingKeyEventColumns = (
  configured: ReadonlyArray<string> | null | undefined,
  rows: ReadonlyArray<{ keyEvents: Record<string, number> }>,
): string[] => {
  if (configured) return [...configured];
  return rows.length ? Object.keys(rows[0].keyEvents) : [];
};

/** Число события строки; нет такого события в ответе — `null` (прочерк). */
export const marketingKeyEventValue = (
  keyEvents: Record<string, number>,
  name: string,
): number | null =>
  Object.prototype.hasOwnProperty.call(keyEvents, name)
    ? marketingNumber(keyEvents[name])
    : null;

/* ── Настройки поиска ────────────────────────────────────────────────── */

/** Имя события GA4 — как проверяет сервер. */
export const MARKETING_GA4_EVENT_RE = /^[A-Za-z0-9_]+$/;

/** Сколько ключевых событий принимает сервер. */
export const MARKETING_MAX_KEY_EVENTS = 20;

/**
 * Список событий из поля-чипов: без пробелов по краям, пустых и повторов —
 * как нормализует сервер. Порядок ввода сохраняется.
 */
export const marketingNormalizeKeyEvents = (
  values: ReadonlyArray<string>,
): string[] =>
  values
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index);

export type MarketingKeyEventsIssue = 'bad_event_name' | 'too_many';

/** Что не так со списком событий; `null` — можно сохранять. */
export const marketingKeyEventsIssue = (
  values: ReadonlyArray<string>,
): { code: MarketingKeyEventsIssue; name?: string } | null => {
  const bad = values.find((value) => !MARKETING_GA4_EVENT_RE.test(value));
  if (bad !== undefined) return { code: 'bad_event_name', name: bad };
  if (values.length > MARKETING_MAX_KEY_EVENTS) return { code: 'too_many' };
  return null;
};

/** Одинаковы ли два списка событий (порядок важен: колонки идут по нему). */
export const marketingSameList = (
  a: ReadonlyArray<string>,
  b: ReadonlyArray<string>,
): boolean => a.length === b.length && a.every((item, i) => item === b[i]);

/** Сколько хостов своего сайта принимает сервер. */
export const MARKETING_MAX_SITE_HOSTS = 50;

/** Метка имени хоста: [a-z0-9-] до 63 символов, без «-» по краям. */
const HOST_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Имя хоста — как проверяет сервер: метки через точку (хотя бы одна
 * точка), всего не длиннее 253; без схемы, пути и порта.
 */
export const marketingIsHostname = (value: string): boolean => {
  if (!value || value.length > 253) return false;
  const labels = value.split('.');
  return (
    labels.length >= 2 && labels.every((label) => HOST_LABEL_RE.test(label))
  );
};

/**
 * Хосты из поля-чипов: нижний регистр, без пробелов и точки в конце, без
 * пустых и повторов — как нормализует сервер.
 */
export const marketingNormalizeHosts = (
  values: ReadonlyArray<string>,
): string[] =>
  marketingNormalizeKeyEvents(
    values.map((value) => value.trim().toLowerCase().replace(/\.+$/, '')),
  );

export type MarketingHostsIssue = 'bad_host' | 'required' | 'too_many';

/** Что не так со списком хостов; `null` — можно сохранять. */
export const marketingHostsIssue = (
  values: ReadonlyArray<string>,
): { code: MarketingHostsIssue; name?: string } | null => {
  const bad = values.find((value) => !marketingIsHostname(value));
  if (bad !== undefined) return { code: 'bad_host', name: bad };
  if (values.length === 0) return { code: 'required' };
  if (values.length > MARKETING_MAX_SITE_HOSTS) return { code: 'too_many' };
  return null;
};

/* ── GA4: группа каналов по умолчанию нашими словами (повторное ревью B5) ─ */

/** GA4 Default Channel Group (как отдаёт API) → ключ подписи. */
const GA4_CHANNEL_KEYS: Readonly<Record<string, string>> = {
  'organic search': 'organicSearch',
  'paid search': 'paidSearch',
  direct: 'direct',
  referral: 'referral',
  'organic social': 'organicSocial',
  'paid social': 'paidSocial',
  email: 'email',
  affiliates: 'affiliates',
  display: 'display',
  'organic video': 'organicVideo',
  'paid video': 'paidVideo',
  'cross-network': 'crossNetwork',
  'organic shopping': 'organicShopping',
  'paid shopping': 'paidShopping',
  sms: 'sms',
  'mobile push notifications': 'push',
  audio: 'audio',
  'paid other': 'paidOther',
  unassigned: 'unassigned',
};

/**
 * Ключ подписи группы каналов GA4 (`marketing.search.traffic.ga4Channels.*`):
 * «Organic Search» по-английски среди наших подписей каналов читался чужим.
 * Незнакомая группа — `null`: экран печатает её как есть, приглушённо.
 */
export const marketingGa4ChannelKey = (
  group: string | null | undefined,
): string | null => {
  const key = GA4_CHANNEL_KEYS[(group ?? '').trim().toLowerCase()];
  return key ? `marketing.search.traffic.ga4Channels.${key}` : null;
};
